import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Building2 } from 'lucide-react'

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      // Check for scroll OR if we are not on the home page (for consistent header on inner pages)
      if (window.scrollY > 50 || location.pathname !== '/') {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    // Run once on mount to set initial state correctly
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Projects', path: '/#projects' }, // Hash link for home section
    { name: 'Blogs', path: '/blogs' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${isScrolled
        ? 'bg-white shadow-md py-2'
        : 'bg-transparent py-4'
        } `}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">

          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="Singh's Best Buy" className="h-10 sm:h-12 w-auto object-contain" />
              <span className={`font-bricolage font-bold text-lg sm:text-2xl tracking-tighter whitespace-nowrap ${isScrolled ? 'text-black' : 'text-white'
                } `}>
                SINGH'S <span className="text-primary-500">BEST BUY</span>
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              link.name === 'Projects' && location.pathname === '/' ? (
                <a
                  key={link.name}
                  href={link.path}
                  className={`text-base font-medium transition-colors hover:text-primary-500 ${isScrolled ? 'text-gray-900' : 'text-white'}`}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`text-base font-medium transition-colors hover:text-primary-500 ${isScrolled ? 'text-gray-900' : 'text-white'}`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </div>

          {/* Contact / CTA */}
          <div className="hidden md:flex items-center gap-4">
            <a
              href="tel:+918368225412"
              className={`flex items-center gap-2 font-medium ${isScrolled ? 'text-gray-900' : 'text-white'
                } `}
            >
              <span className="text-primary-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </span>
              +91 83682-25412
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 rounded-md ${isScrolled ? 'text-gray-900' : 'text-white'} `}
            >
              <span className="sr-only">Open menu</span>
              {/* Hamburger Icon */}
              <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {
        isMobileMenuOpen && (
          <div className="md:hidden bg-white shadow-lg absolute w-full top-full left-0 border-t">
            <div className="px-4 pt-2 pb-6 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="block px-3 py-2 text-base font-medium text-gray-900 hover:text-primary-500 hover:bg-gray-50 rounded-md"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <a
                href="tel:+918368225412"
                className="block w-full text-center mt-4 bg-primary-500 text-white px-4 py-2 rounded-md font-medium"
              >
                Call Now
              </a>
            </div>
          </div>
        )
      }
    </nav >
  )
}

export default Navbar
